import { Link } from 'react-router-dom';
import { useLanguage } from '../../core/hooks/use-language';
import { useTranslation } from '../../core/hooks/use-translation';
import type { MeetingFormat } from '../../core/models/meeting-format';
import { CheckIcon, HomeIcon } from './booking-icons';
import { BookingSummary } from './booking-summary';

interface BookingSuccessProps {
    serviceName: string;
    specialistName: string | null;
    format: MeetingFormat;
    /** `"YYYY-MM-DD"`, or null for an online request, which holds no time. */
    date: string | null;
    /** The wire value, `"HH:mm:ss"`; shortened for display. */
    time: string | null;
    slotHeld: boolean;
}

/**
 * The confirmation screen. It renders from what the browser already knows, because the response
 * carries no identifier — which is what makes "the submission returns nothing" free rather than a
 * gap to work around.
 *
 * **What it promises depends on whether a slot was actually held.** The live page says the same
 * sentence either way; here a request that reserved a time says so, and one that did not says staff
 * will ring to agree one — which is the difference between a customer turning up on Thursday and a
 * customer waiting for a call.
 */
export function BookingSuccess({ serviceName, specialistName, format, date, time, slotHeld }: BookingSuccessProps) {
    const { t } = useTranslation();
    const language = useLanguage();

    return (
        <div className="ok-wrap">
            <div className="ok-icon">
                <CheckIcon />
            </div>
            <h1 className="ok-title">{t('booking.success.title')}</h1>
            <p className="ok-sub">{slotHeld ? t('booking.success.slot_held') : t('booking.success.call_back')}</p>

            <BookingSummary
                serviceName={serviceName}
                specialistName={specialistName}
                format={format}
                date={date}
                time={time}
                variant="chips"
            />

            <Link to={`/${language}`} className="btn-home">
                <HomeIcon />
                <span>{t('booking.success.home')}</span>
            </Link>
        </div>
    );
}
